import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { serviceLabel } from "@/components/booking/constants";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, MapPin, CreditCard, CheckCircle2, XCircle, Clock, UserPlus, LogIn, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";

export default function QuoteView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/quotes/public/${token}`).then((r) => setQuote(r.data)).catch(() => setQuote(false)), [token]);
  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/public/${token}/accept`);
      if (data.billing === "account") {
        toast.success(data.delivered ? "Approved — the delivery is billed to your account." : "Approved — booking confirmed on your account.");
        navigate(`/orders/${data.order_id}`);
        return;
      }
      const pay = await api.post("/payments/checkout", { order_id: data.order_id, origin_url: window.location.origin });
      if (pay.data.paid_by_credit) {
        toast.success("Paid in full with your referral credit — no card needed!");
        navigate(`/orders/${data.order_id}`);
        return;
      }
      window.location.href = pay.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't accept the quote");
      setBusy(false);
      load();
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/quotes/public/${token}/decline`);
      toast.success("Quote declined");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't decline the quote"); }
    finally { setBusy(false); }
  };

  if (quote === null) return <Shell><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></Shell>;
  if (quote === false) return <Shell><p className="py-16 text-center text-muted-foreground" data-testid="quote-not-found">This quote link isn't valid.</p></Shell>;

  const isMine = user && quote.assigned && quote.assigned_to_me;
  const isCustomer = user && user.role === "customer";
  const canAct = quote.status === "open" && user && (isCustomer || user.role === "admin") && (!quote.assigned || isMine);
  const expiryStr = new Date(quote.expires_at).toLocaleDateString("en-GB");

  return (
    <Shell>
      <Card className="border-kilted-green/20" data-testid="quote-view-card">
        <CardHeader>
          <p className="text-xs font-semibold uppercase tracking-widest text-kilted-gold">Delivery quote{quote.tracking_number ? ` · ${quote.tracking_number}` : ""}</p>
          <CardTitle className="font-heading text-2xl font-extrabold text-kilted-green" data-testid="quote-view-total">
            ${quote.price.total.toFixed(2)} <span className="text-sm font-medium text-muted-foreground">inc GST</span>
          </CardTitle>
          <p className="text-sm text-muted-foreground">Prepared by {quote.created_by_name} · valid until {expiryStr}</p>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div className="space-y-3 rounded-lg border border-border p-4">
            <Addr label="Pickup" a={quote.pickup} />
            <div className="ml-2 h-4 border-l-2 border-dashed border-border" />
            <Addr label="Drop-off" a={quote.dropoff} />
          </div>

          <div className="space-y-1">
            {quote.items.map((it, i) => (
              <div key={`${it.size}-${i}`} className="flex justify-between">
                <span className="capitalize">{it.quantity}× {it.size}{it.weight_kg ? ` · ${it.weight_kg}kg` : ""}</span>
              </div>
            ))}
            <div className="flex justify-between text-muted-foreground"><span>Service</span><span className="capitalize">{serviceLabel(quote.service)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Distance</span><span>{quote.distance_km} km</span></div>
            {quote.price.fuel_levy ? <div className="flex justify-between text-muted-foreground"><span>Fuel levy ({quote.price.fuel_levy_pct}%)</span><span>${quote.price.fuel_levy.toFixed(2)}</span></div> : null}
            <div className="flex justify-between text-muted-foreground"><span>GST</span><span>${quote.price.gst.toFixed(2)}</span></div>
          </div>

          {quote.status !== "open" && (
            <StatusNote status={quote.status} data-testid="quote-status-note" />
          )}

          {!canAct && quote.status === "open" && quote.assigned && !isMine && user && user.role !== "driver" && (
            <p className="rounded-md bg-amber-50 p-3 text-amber-700" data-testid="quote-wrong-account">This quote is reserved for a specific account holder.</p>
          )}

          {quote.status === "open" && !user && (
            <div className="space-y-2 pt-2">
              <p className="text-muted-foreground">
                {quote.assigned
                  ? "Sign in to your account to review and approve this delivery."
                  : "Create a free account (or sign in) to view and book this delivery."}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => navigate(`/welcome?quote=${token}&tab=register`)} data-testid="quote-register-button" className="gap-1.5 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight"><UserPlus className="h-4 w-4" /> Create account</Button>
                <Button variant="outline" onClick={() => navigate(`/welcome?quote=${token}`)} data-testid="quote-login-button" className="gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10"><LogIn className="h-4 w-4" /> Log in</Button>
              </div>
            </div>
          )}

          {canAct && (
            <div className="space-y-2 pt-2">
              {quote.assigned && isMine ? (
                <>
                  <p className="text-muted-foreground">{quote.delivery_state === "delivered"
                    ? "This delivery was made on your behalf. Approve it to have it billed to your monthly account."
                    : "Approve this booking to have it billed to your monthly account."}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={accept} disabled={busy} data-testid="quote-approve-button" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve & bill to account
                    </Button>
                    <Button variant="outline" onClick={decline} disabled={busy} data-testid="quote-decline-button" className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50">
                      <XCircle className="h-4 w-4" /> Decline
                    </Button>
                  </div>
                </>
              ) : (
                <Button onClick={accept} disabled={busy} data-testid="quote-book-pay-button" className="w-full gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Book & pay ${quote.price.total.toFixed(2)}
                </Button>
              )}
            </div>
          )}

          {user && user.role === "driver" && (
            <p className="text-xs text-muted-foreground">You're viewing this as a driver — share the link with the customer to book it.</p>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
}

function Shell({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dash = user ? (user.role === "admin" ? "/admin" : user.role === "driver" ? "/driver" : "/dashboard") : null;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white px-4 py-3">
        <div className="mx-auto flex max-w-lg items-center justify-between">
          <Logo />
          {dash && (
            <Button variant="ghost" size="sm" onClick={() => navigate(dash)} data-testid="quote-dashboard-link" className="gap-1.5 text-kilted-green">
              <LayoutDashboard className="h-4 w-4" /> Dashboard
            </Button>
          )}
        </div>
      </header>
      <main className="mx-auto max-w-lg px-4 py-8">{children}</main>
    </div>
  );
}

function Addr({ label, a }) {
  return (
    <div className="flex gap-2">
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-kilted-gold" />
      <div>
        <p className="text-xs font-semibold uppercase text-muted-foreground">{label}</p>
        {a.business && <p className="font-semibold">{a.business}</p>}
        <p>{[a.line1, a.city, a.postcode].filter(Boolean).join(", ")}</p>
      </div>
    </div>
  );
}

function StatusNote({ status }) {
  const map = {
    approved: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700", msg: "This quote has been approved and billed to account." },
    booked: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700", msg: "This quote has already been booked." },
    declined: { icon: XCircle, cls: "bg-red-50 text-red-600", msg: "This quote was declined." },
    expired: { icon: Clock, cls: "bg-gray-100 text-gray-600", msg: "This quote has expired — contact The Kilted Courier for a fresh price." },
  };
  const m = map[status] || map.expired;
  const Icon = m.icon;
  return <p className={`flex items-center gap-2 rounded-md p-3 ${m.cls}`} data-testid="quote-status-note"><Icon className="h-4 w-4 shrink-0" /> {m.msg}</p>;
}
